import { useCallback, useEffect, useMemo, useState } from 'react';
import { Topbar } from '@/components/Topbar';
import { SkipLink } from '@/components/SkipLink';
import { CourseLink } from '@/components/CourseLink';
import { NavShell } from '@/components/NavShell';
import { NudgeCard, PracticeBench, WeekRhythm } from '@/components/PracticeBench';
import type { BenchRec, BenchWeakAxis, RhythmDay } from '@/components/PracticeBench';
import { BUDDY_VARIANTS, rotatingBuddyVariant } from '@/components/buddies';
import type { BuddyVariant } from '@/components/buddies';
import { useTheme } from '@/contexts/ThemeContext';
import { useAuthContextOptional } from '@/contexts/AuthContext';
import { homeworkV2BySprintId } from '@/data/homework-v2';
import type { HomeworkV2 } from '@/data/homework-v2';
import { isSprintLockedForUser, roleOf } from '@/lib/access';
import { axisOf, computePracticeStats, dayKey, pomodoroMinutesByDay } from '@/lib/practiceStats';
import {
  daysBetween,
  deriveNudge,
  dismissNudge,
  readNudgesState,
  reasonText,
  recordNudgeShown,
  rhythmCaption,
  writeNudgesState,
} from '@/lib/nudges';
// H.4-EXT2.1 (17 MAY 2026): Buddy import removed · was used inside dash-side
// aside (.buddy-nudge avatar) · entire aside dropped per Genie pick «удалить
// правую колонку Dashboard» (duplicates KPI strip).
import { useProgress } from '@/contexts/ProgressContext';
import { ticketsBySprint, ticketById } from '@shared/data/tickets';
import { SPRINTS } from '@shared/data/sprints';
import {
  activeSprint as computeActiveSprint,
  resumeTicketId,
  sprintRequiredPct,
} from '@/lib/sprint-progression';
import { quizBankExists } from '@shared/data/quiz-bank-ids';
import { shortSprintTitle } from '@/lib/sprint-meta';
import type { TicketMeta } from '@/types';

/**
 * Dashboard screen · RISO_FINAL_DESIGN Phase B (16 MAY 2026).
 *
 * Merger: поглощает Morning screen content per slot_riso_dashboard_welcome brief.
 * Source design: `04_DESIGN/Riso-polished-final/polished_prototype_riso/screens-dashboard.jsx`.
 *
 * Composition (top → bottom):
 *   1. dash-hero — date pill + greet + subtext (empty/active branches)
 *   2. kpi-strip — 4 KPIs (В работе · На ревью · За неделю · Buddy подсказок)
 *   3. dash-bridge — «Вчера → Сегодня» 3-col (absorbed from Morning · empty placeholder)
 *   4. dash-cols 2-col (slot_home_practice_widget · 10 JUL 2026):
 *      - dash-main-col > dash-focus «Три шага» + WeekRhythm «Ритм недели»
 *      - PracticeBench aside — верстак практики + слот подсказки бадди
 *        (гибрид B+C · спека COORDINATION/specs/home_practice_widget_copy.md)
 *      - (историческое) dash-main-col > dash-focus — «Три шага»
 *      - dash-side stack:
 *        - buddy-nudge (inline Buddy avatar 40px)
 *        - dash-mini «Сегодня» (4 stat rows)
 *        - week-spark (absorbed from Morning · 7-day SVG)
 *        - lessons-progress (absorbed from Morning · top 3 sprints)
 *        - dash-quote (inspirational)
 *
 * dashState = `'empty' | 'active'` derived from progress (no in-progress AND no done → empty).
 *
 * Dropped from W2 Dashboard (re-homed elsewhere per Q-1 E + Phase F plan):
 *   - Активные тикеты Card (→ focus-plan), Спринты Card (→ Progress · Phase D),
 *     AtelierRailCard/Modal (→ Atelier overlay), BonusLessons/Skills/CTFL/Buddy Card (→ Phase F).
 *
 * Dropped from W2 Morning (absorbed here):
 *   - eyebrow/h1/sub → hero; continue-card → collapsed в focus-step #01;
 *     bridge → top-level dash-bridge; morning-buddy figure → inline в buddy-nudge;
 *     morning-actions → dropped (focus-step click covers).
 *
 * Route alias `morning` → Dashboard deferred to Phase G cleanup.
 */
interface DashboardProps {
  onLogo: () => void;
  onTicket: (id: string) => void;
  onSprint: (id: string) => void;
  /** Optional handlers preserved для App.tsx wiring compat · ignored в B.2 · re-home Phase F. */
  onPractice?: (scenarioId: string) => void;
  onCapstone?: () => void;
  onProfile?: () => void;
  onMiniLesson?: (lessonId: string) => void;
  /**
   * NavShell routing callbacks · 17 MAY 2026 hotfix (Phase B miss · Dashboard
   * absorbed Morning but forgot NavShell wrap · only Welcome got wrap). All
   * fallback к onLogo (Dashboard) when not provided. Pattern matches Sprint.tsx.
   */
  onSprints?: () => void;
  onProgress?: () => void;
  /** Open the active sprint's final test · slot_progression_plan_quiz (2 JUN 2026).
   *  Used when all tickets are closed but the test isn't passed yet. */
  onFinalTest?: (sprintId: string) => void;
  /**
   * SBM merge (18 MAY 2026 · slot_sprint_board_merge): `onBoard` prop dropped.
   * NavShell 'board' nav key no longer emitted (Sidebar NAV_ITEMS reshape).
   * Dashboard «Доска» click routes к 'sprint' screen (single nav destination).
   */
  onBuddy?: () => void;
  /**
   * Fallback for nav keys this screen's handleNav doesn't handle itself
   * (e.g. 'practice', 'portfolio') — App passes dispatchScreenNav so new
   * screens route from everywhere without re-touching each screen's switch.
   * slot_homework_rework Ф1 (16 JUN 2026).
   */
  onNavFallback?: (key: string) => void;
}

const DAY_OF_WEEK_RU: Record<number, string> = {
  0: 'воскресенье',
  1: 'понедельник',
  2: 'вторник',
  3: 'среда',
  4: 'четверг',
  5: 'пятница',
  6: 'суббота',
};

// H.4-EXT2.1 (17 MAY 2026): DAY_SHORT_RU removed · was used by week-spark SVG
// inside dash-side aside (retired).

const startOfDayMs = (d: Date) => {
  const c = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  return c.getTime();
};

// H.4-EXT2.2 (17 MAY 2026): standard Pomodoro session length (matches
// production Pomodoro.tsx default · 25 min focus). Used by «Время в фокусе»
// KPI to convert session count к minutes.
const POMODORO_MINUTES_PER_SESSION = 25;

const todayDayIndex = () => {
  const js = new Date().getDay();
  // Convert JS Sun=0 to ISO Mon=0
  return (js + 6) % 7;
};

/**
 * Map TicketMeta type to focus-step tag label.
 * Aligns с new design tags (УРОК / BUG / PRACTICE / etc.).
 */
function typeToTag(t: TicketMeta): { label: string; badge: string } {
  switch (t.type) {
    case 'docs':
    case 'task':
    case 'feature':
      return { label: 'УРОК', badge: 'lesson' };
    case 'master':
      return { label: 'MASTER', badge: 'master' };
    case 'bug':
      return { label: 'BUG', badge: 'bug' };
    case 'practice':
    case 'mission':
    case 'lab':
      return { label: 'PRACTICE', badge: 'practice' };
    case 'test':
    case 'exam':
      return { label: 'TEST', badge: 'test' };
    case 'ai':
      return { label: 'AI', badge: 'ai' };
    case 'design':
      return { label: 'DESIGN', badge: 'design' };
    case 'metrics':
      return { label: 'METRICS', badge: 'metrics' };
    default:
      return { label: 'УРОК', badge: 'lesson' };
  }
}

/**
 * Compute % progress for an in-progress ticket from AC checks.
 * Returns 0 if ticket not started or no AC defined.
 */
function ticketProgressPct(t: TicketMeta, acChecked: Record<string, boolean> | undefined): number {
  if (!acChecked) return 0;
  const total = t.ac.length;
  if (total === 0) return 0;
  const done = t.ac.filter((a) => acChecked[a.id]).length;
  return Math.round((done / total) * 100);
}

export function Dashboard({
  onLogo,
  onTicket,
  onSprint,
  onSprints,
  onProgress,
  onBuddy,
  onFinalTest,
  onNavFallback,
}: DashboardProps) {
  const { progress } = useProgress();

  // Active sprint + resume ticket
  const activeSprint = useMemo(() => computeActiveSprint(progress), [progress]);
  const activeSprintId = activeSprint.id;

  // NavShell click routing · pass-through к existing callbacks (17 MAY hotfix).
  // H.4-EXT.3 (17 MAY 2026 slot_riso_phase_h_final_smoke):
  // 'sprint' + 'board' previously silent no-op (onBoard prop never wired from
  // App.tsx · Genie clicked Доска nav и nothing happened). Fixed: route к onSprint
  // with activeSprintId as fallback. Board view lives inline в Sprint.tsx per
  // Phase C design (Sprint.tsx imports { Board } from '@/screens/Board').
  const handleNav = useCallback(
    (key: string) => {
      switch (key) {
        case 'dashboard':
          // Already here · no-op.
          break;
        case 'sprint':
          // Route к active sprint (was: silent fallback к onLogo).
          onSprint(activeSprintId);
          break;
        case 'sprints':
          (onSprints ?? onLogo)();
          break;
        case 'progress':
          (onProgress ?? onLogo)();
          break;
        // SBM merge (18 MAY 2026): 'board' case retired · NavShell no longer
        // emits 'board' key (Sidebar item merged into 'sprint' с label «Доска»).
        case 'buddy':
          if (onBuddy) onBuddy();
          break;
        case 'atelier':
          // Handled internally by NavShell (overlay toggle).
          break;
        default:
          // 'practice', 'portfolio', and any future key route via App's dispatcher.
          onNavFallback?.(key);
          break;
      }
    },
    [onLogo, onSprint, onSprints, onProgress, onBuddy, activeSprintId, onNavFallback],
  );
  const resumeId = useMemo(() => resumeTicketId(progress), [progress]);

  // KPI counts (real data)
  const inProgressCount = useMemo(
    () => Object.values(progress.tickets).filter((t) => t.status === 'in-progress').length,
    [progress.tickets],
  );
  // H.4-EXT2.2 (17 MAY 2026): reviewCount derivation removed · sole consumers
  // were «На ревью» KPI card (replaced с «Время в фокусе») + dash-mini «На ревью»
  // row (dash-side aside dropped per H.4-EXT2.1).
  const completedCount = useMemo(
    () => Object.values(progress.tickets).filter((t) => t.status === 'done').length,
    [progress.tickets],
  );

  // dashState derivation
  const dashState: 'empty' | 'active' = useMemo(() => {
    if (inProgressCount === 0 && completedCount === 0) return 'empty';
    return 'active';
  }, [inProgressCount, completedCount]);
  const empty = dashState === 'empty';

  // slot_t00_ftue_rewrite (6 JUN 2026): dominant first-run CTA. Visible while
  // the student has closed nothing yet AND T00 itself isn't done (opening T00
  // flips dashState to 'active', so `empty` alone would hide the card too early).
  const startHereVisible =
    completedCount === 0 && progress.tickets['T00']?.status !== 'done';

  // Recent done · bridge «Вчера». Excludes TODAY's completions — «Вчера» means
  // before today, so a ticket closed today belongs to «Сегодня», not here
  // (Genie 14 AUG: сегодняшние закрытия висели под «Вчера»).
  const recentDone = useMemo<TicketMeta[]>(() => {
    const todayStart = startOfDayMs(new Date());
    return Object.entries(progress.tickets)
      .filter(
        ([, p]) =>
          p.status === 'done' &&
          p.completedAt &&
          new Date(p.completedAt).getTime() < todayStart,
      )
      .sort((a, b) => (b[1].completedAt ?? '').localeCompare(a[1].completedAt ?? ''))
      .slice(0, 3)
      .map(([id]) => ticketById(id))
      .filter((t): t is TicketMeta => t !== undefined);
  }, [progress.tickets]);

  // Focus plan · top 3 active tickets из activeSprint
  // Priority: in-progress first, then todo, sliced 3
  const focusSteps = useMemo<TicketMeta[]>(() => {
    const sprintTickets = ticketsBySprint(activeSprintId);
    const ticketStatus = (id: string) => progress.tickets[id]?.status ?? 'todo';
    const inProgress = sprintTickets.filter((t) => ticketStatus(t.id) === 'in-progress');
    const todo = sprintTickets.filter((t) => ticketStatus(t.id) === 'todo');
    // «План на сегодня» dynamic fix (2 JUN 2026): exclude 'review'. Lessons now
    // auto-complete to 'done' (no reviewer · see useTicketState), so a worked-through
    // step leaves the plan immediately instead of parking at #01 «нужно ревью».
    // Any non-lesson ticket that legitimately parks in review also drops out of the
    // plan rather than freezing it (per Genie pick «оба: авто-done + чистый план»).
    return [...inProgress, ...todo].slice(0, 3);
  }, [progress.tickets, activeSprintId]);

  // Today queue (Bridge «Сегодня в очереди») · in-progress excluding resume + top todo
  const todayQueue = useMemo<TicketMeta[]>(() => {
    const inProg = Object.entries(progress.tickets)
      .filter(([id, p]) => p.status === 'in-progress' && id !== resumeId)
      .slice(0, 2)
      .map(([id]) => ticketById(id))
      .filter((t): t is TicketMeta => t !== undefined);
    return inProg;
  }, [progress.tickets, resumeId]);

  // H.4-EXT2.1 (17 MAY 2026): weekActivity / weekTotal / weekMax / lessonsRows
  // derivations removed · sole consumers were week-spark SVG + lessons-progress
  // blocks inside dash-side aside (entire aside dropped per Genie pick).
  // Week-scoped weekTotal STILL needed by KPI strip «За неделю» card · compact
  // re-derivation below replaces full weekActivity array (saves the per-day map).
  const weekTotal = useMemo(() => {
    const now = new Date();
    const todayMs = startOfDayMs(now);
    const weekStartMs = todayMs - 6 * 86_400_000; // last 7 days inclusive of today
    let count = 0;
    Object.values(progress.tickets).forEach((tp) => {
      if (tp.status !== 'done' || !tp.completedAt) return;
      const compMs = startOfDayMs(new Date(tp.completedAt));
      if (compMs >= weekStartMs && compMs <= todayMs) count += 1;
    });
    return count;
  }, [progress.tickets]);

  // H.4-EXT2.2 (17 MAY 2026): Pomodoro focus minutes today · derived from
  // progress.achievements.pomodoroSessions[]. Each session = POMODORO_MINUTES_PER_SESSION
  // (25 min canonical). Filters sessions where `at` timestamp >= start-of-today.
  const focusMinutesToday = useMemo(() => {
    const todayMs = startOfDayMs(new Date());
    const sessions = progress.achievements?.pomodoroSessions ?? [];
    return sessions.filter((s) => s.at >= todayMs).length * POMODORO_MINUTES_PER_SESSION;
  }, [progress.achievements?.pomodoroSessions]);

  // H.4-EXT2.2 (17 MAY 2026): Course progress · % of all course tickets closed.
  // Denominator = sum of all sprint.ticketIds.length across SPRINTS[]. Stable
  // (sprints metadata static) so useMemo with empty deps OK.
  // slot_s06_rework (11 JUN 2026): optional-спринты (S06) не входят в прогресс
  // курса — их тикеты исключены и из знаменателя, и из числителя (100% без S06).
  const optionalTicketIds = useMemo(
    () => new Set(SPRINTS.filter((sp) => sp.optional).flatMap((sp) => sp.ticketIds)),
    [],
  );
  const totalTicketsCount = useMemo(
    () => SPRINTS.filter((sp) => !sp.optional).reduce((sum, sp) => sum + sp.ticketIds.length, 0),
    [],
  );
  const coreCompletedCount = useMemo(
    () =>
      Object.entries(progress.tickets).filter(
        ([id, t]) => t.status === 'done' && !optionalTicketIds.has(id),
      ).length,
    [progress.tickets, optionalTicketIds],
  );
  const coursePct = totalTicketsCount > 0
    ? Math.round((coreCompletedCount / totalTicketsCount) * 100)
    : 0;

  // Date meta для hero
  const now = new Date();
  const dayNum = now.getDate().toString().padStart(2, '0');
  const todayLabel = DAY_OF_WEEK_RU[(todayDayIndex() + 1) % 7];
  // slot_vocabulary_navbar (17 MAY 2026 · authorized unblock fix during VN
  // verify chain): removed vestigial `sprintNum` + `sprintDay` declarations
  // (Phase B Dashboard merger refactor leftover · never consumed in JSX
  // post-merger). TS6133 blocked verify · Genie authorized clean delete.

  // First focus step с continue-card metadata (collapsed per Q-B1-4 a)
  const firstStep = focusSteps[0];
  const firstStepPct = firstStep
    ? ticketProgressPct(firstStep, progress.tickets[firstStep.id]?.acChecked)
    : 0;
  const firstStepMinLeft = firstStep
    ? Math.max(1, Math.round(firstStep.estimateMin * ((100 - firstStepPct) / 100)))
    : 0;

  // slot_progression_plan_quiz (2 JUN 2026): when every required ticket of the
  // active sprint is closed but the final test isn't passed, «План на сегодня»
  // surfaces a «сдать финальный тест» step instead of going blank. activeSprint
  // is by definition not-complete, so requiredDone + hasQuiz here can only mean
  // the final test is still pending.
  const activeHasQuiz = useMemo(
    () => quizBankExists(activeSprint.finalTestBankId),
    [activeSprint.finalTestBankId],
  );
  const activeRequiredDone = useMemo(
    () => sprintRequiredPct(progress, activeSprintId) === 100,
    [progress, activeSprintId],
  );
  const finalTestPending = !empty && focusSteps.length === 0 && activeHasQuiz && activeRequiredDone;

  // ══ Виджет практики (10 JUL 2026 · slot_home_practice_widget) ══════════
  // Гибрид «Верстак дня + подсказки бадди» + «Ритм недели» по мокапу
  // mockup_home_homework_hybrid.html и спеке home_practice_widget_copy.md
  // [ack Genie · 1в/2а/3а]. Деривация зеркалит PracticeSpace (practiceStats).

  // Auth опционален: Dashboard рендерится и без AuthProvider (тесты/смоки) —
  // тогда роль = DEFAULT_ROLE, гейтинг спринтов как для free.
  const authCtx = useAuthContextOptional();
  const role = roleOf(authCtx?.user);
  const { avatarBuddy } = useTheme();
  const buddyVar: BuddyVariant =
    avatarBuddy && avatarBuddy in BUDDY_VARIANTS
      ? (avatarBuddy as BuddyVariant)
      : rotatingBuddyVariant();

  const availableHw = useMemo<HomeworkV2[]>(() => {
    const out: HomeworkV2[] = [];
    for (const sprint of SPRINTS) {
      const hws = homeworkV2BySprintId(sprint.id);
      if (hws.length === 0) continue;
      if (isSprintLockedForUser(progress, sprint.id, role)) continue;
      out.push(...hws);
    }
    return out;
  }, [progress, role]);

  const practiceStats = useMemo(
    () =>
      computePracticeStats({
        available: availableHw,
        progressHw: progress.homework ?? {},
        sprints: SPRINTS,
        activeSprintId,
        activityMinByDay: Object.fromEntries(
          Object.entries(progress.achievements?.activityByDay ?? {}).map(([k, sec]) => [
            k,
            Math.round((sec as number) / 60),
          ]),
        ),
        pomodoroMinByDay: pomodoroMinutesByDay(progress.achievements?.pomodoroSessions ?? []),
      }),
    [availableHw, progress, activeSprintId],
  );

  const todayKey = dayKey(now);
  const yesterdayKey = dayKey(new Date(now.getTime() - 86_400_000));
  const [nudgesState, setNudgesState] = useState(readNudgesState);

  const hwById = useMemo(() => new Map(availableHw.map((h) => [h.id, h])), [availableHw]);

  // E1 · сдано в последние 48ч → разбор открыт (dismiss по id = навсегда)
  const razborHw = useMemo(() => {
    const cutoff = Date.now() - 48 * 3_600_000;
    const done = Object.entries(progress.homework ?? {})
      .filter(([id, p]) => {
        if (p?.status !== 'done' || !p.completedAt || !hwById.has(id)) return false;
        if (nudgesState.dismissed[`razbor:${id}`]) return false;
        return new Date(p.completedAt).getTime() >= cutoff;
      })
      .sort((a, b) => (b[1]?.completedAt ?? '').localeCompare(a[1]?.completedAt ?? ''));
    const latestId = done[0]?.[0];
    return latestId ? (hwById.get(latestId) ?? null) : null;
  }, [progress.homework, hwById, nudgesState.dismissed]);

  const activeSprintHw = useMemo(
    () => availableHw.filter((h) => h.sprintId === activeSprintId),
    [availableHw, activeSprintId],
  );

  // E2 · пачка активного спринта не тронута
  const newPack = useMemo(() => {
    if (activeSprintHw.length === 0) return null;
    const touched = activeSprintHw.some((h) => {
      const s = progress.homework?.[h.id]?.status;
      return s === 'in-progress' || s === 'done';
    });
    if (touched) return null;
    const first = activeSprintHw[0];
    if (!first) return null;
    return {
      sprintId: activeSprintId,
      count: activeSprintHw.length,
      theme: activeSprint.category ?? shortSprintTitle(activeSprint.title),
      first,
      lessonId: focusSteps[0]?.id ?? null,
    };
  }, [activeSprintHw, progress.homework, activeSprintId, activeSprint]);

  // E3 · слабейшая ось <40% при ≥3 задачах + доступная задача по ней
  const weakAxisInput = useMemo(() => {
    const w = practiceStats.weakest;
    if (!w || w.total < 3 || w.value >= 0.4) return null;
    const hwCand = availableHw.find(
      (h) => axisOf(h) === w.axis && progress.homework?.[h.id]?.status !== 'done',
    );
    return hwCand ? { axis: w, hw: hwCand } : null;
  }, [practiceStats.weakest, availableHw, progress.homework]);

  // E4 · серия ≥3 дней, сегодня пусто (решение 1в: без окна по времени)
  const streakInput = useMemo(
    () =>
      practiceStats.streak >= 3 && practiceStats.daily.activeMin === 0
        ? { days: practiceStats.streak, estMin: practiceStats.next?.hw.estimateMin ?? 15 }
        : null,
    [practiceStats],
  );

  const nudge = useMemo(
    () =>
      deriveNudge({
        todayKey,
        yesterdayKey,
        state: nudgesState,
        razborHw,
        newPack,
        weakAxis: weakAxisInput,
        streak: streakInput,
      }),
    [todayKey, yesterdayKey, nudgesState, razborHw, newPack, weakAxisInput, streakInput],
  );

  // Фиксация показа. recordNudgeShown идемпотентен → StrictMode-safe
  // (повторный вызов возвращает тот же ref, setState бэйлится).
  useEffect(() => {
    if (!nudge) return;
    setNudgesState((prev) => {
      const next = recordNudgeShown(prev, nudge, todayKey);
      if (next !== prev) writeNudgesState(next);
      return next;
    });
  }, [nudge, todayKey]);

  const goPractice = useCallback(() => onNavFallback?.('practice'), [onNavFallback]);

  const handleNudgeDismiss = useCallback(() => {
    if (!nudge) return;
    setNudgesState((prev) => {
      const next = dismissNudge(prev, nudge, todayKey);
      writeNudgesState(next);
      return next;
    });
  }, [nudge, todayKey]);

  const handleNudgeAction = useCallback(() => {
    handleNudgeDismiss();
    goPractice();
  }, [handleNudgeDismiss, goPractice]);

  // Рекомендация верстака · R1 resume → R2 themed → R3 fresh → R4 tail → R5 clear
  const benchRec = useMemo<BenchRec | null>(() => {
    const next = practiceStats.next;
    if (next) {
      if (next.resume) {
        return {
          kind: 'resume',
          chipLabel: 'Продолжить',
          code: next.hw.code,
          estimateMin: next.hw.estimateMin,
          title: next.hw.title,
          selfCheckDone: next.selfCheckDone,
          selfCheckTotal: next.selfCheckTotal,
          reason: reasonText('resume', { k: next.selfCheckDone, m: next.selfCheckTotal }, todayKey),
          ctaLabel: 'К верстаку →',
        };
      }
      const themedTicket = next.hw.sprintId === activeSprintId ? focusSteps[0]?.id : undefined;
      return {
        kind: 'fresh',
        chipLabel: 'Новая',
        code: next.hw.code,
        estimateMin: next.hw.estimateMin,
        title: next.hw.title,
        reason: themedTicket
          ? reasonText('themed', { ticket: themedTicket }, todayKey)
          : reasonText('fresh', { est: next.hw.estimateMin }, todayKey),
        ctaLabel: 'Начать →',
      };
    }
    const tailHw = availableHw.find(
      (h) => h.sprintId !== activeSprintId && progress.homework?.[h.id]?.status !== 'done',
    );
    if (tailHw) {
      return {
        kind: 'tail',
        chipLabel: 'Хвост',
        code: tailHw.code,
        estimateMin: tailHw.estimateMin,
        title: tailHw.title,
        reason: reasonText('tail', { oldSprint: tailHw.sprintId, est: tailHw.estimateMin }, todayKey),
        ctaLabel: 'К хвосту →',
      };
    }
    return null;
  }, [practiceStats.next, activeSprintId, focusSteps, availableHw, progress.homework, todayKey]);

  const benchClearText = useMemo(() => reasonText('clear', {}, todayKey), [todayKey]);

  const benchWeakAxis = useMemo<BenchWeakAxis | null>(() => {
    const w = practiceStats.weakest;
    return w && w.total > 0
      ? { label: w.label, done: w.done, total: w.total, pct: Math.round(w.value * 100) }
      : null;
  }, [practiceStats.weakest]);

  // Хвост-строка (не дублируем, когда хвост уже в рекомендации)
  const benchTail = useMemo(() => {
    if (benchRec?.kind === 'tail') return null;
    const tails = availableHw.filter(
      (h) => h.sprintId !== activeSprintId && progress.homework?.[h.id]?.status !== 'done',
    );
    const firstTail = tails[0];
    return firstTail ? { count: tails.length, sprintId: firstTail.sprintId } : null;
  }, [availableHw, activeSprintId, progress.homework, benchRec]);

  const benchDoneCount = useMemo(
    () => activeSprintHw.filter((h) => progress.homework?.[h.id]?.status === 'done').length,
    [activeSprintHw, progress.homework],
  );

  // «Ритм недели» · текущая ISO-неделя из heatmap + подпись-состояние
  const rhythm = useMemo(() => {
    const isoDow = todayDayIndex();
    const mondayMs = startOfDayMs(new Date()) - isoDow * 86_400_000;
    const DAY_LABELS = ['пн', 'вт', 'ср', 'чт', 'пт', 'сб', 'вс'];
    const heatByKey = new Map(practiceStats.heatmap.map((c) => [c.dateKey, c] as const));
    const days: RhythmDay[] = [];
    let weekActiveDays = 0;
    for (let i = 0; i < 7; i += 1) {
      const key = dayKey(new Date(mondayMs + i * 86_400_000));
      const cell = heatByKey.get(key);
      const future = i > isoDow;
      const minutes = future ? 0 : (cell?.minutes ?? 0);
      if (minutes > 0) weekActiveDays += 1;
      days.push({
        dateKey: key,
        level: future ? 0 : (cell?.level ?? 0),
        future,
        today: i === isoDow,
        title: minutes > 0 ? `${DAY_LABELS[i] ?? ''} · ${minutes} мин` : (DAY_LABELS[i] ?? ''),
      });
    }
    let gapDays = 0;
    if (practiceStats.daily.activeMin === 0) {
      gapDays = 99;
      const lastActive = practiceStats.heatmap
        .filter((c) => c.minutes > 0)
        .map((c) => c.dateKey)
        .sort()
        .pop();
      if (lastActive) gapDays = Math.min(99, Math.max(0, daysBetween(lastActive, todayKey)));
    }
    const caption = rhythmCaption({
      todayKey,
      goalMin: practiceStats.daily.goalMin,
      activeMinToday: practiceStats.daily.activeMin,
      streak: practiceStats.streak,
      gapDays,
      weekActiveDays,
      dow: isoDow,
    });
    return { days, caption };
  }, [practiceStats, todayKey]);

  // Dynamic plan heading — was hard-coded «Три шага · {title}» even when the
  // plan had 0/1/2 steps or was a final-test prompt. Now reflects the real state.
  const STEP_WORD: Record<number, string> = { 1: 'Один шаг', 2: 'Два шага', 3: 'Три шага' };
  // Short sprint name (drops the «— tagline») so the heading stays compact and
  // matches the FinalTest screen — full marketing title felt hard-coded/verbose.
  const activeName = shortSprintTitle(activeSprint.title);
  const planHeading = empty
    ? 'Старт — открой спринт'
    : focusSteps.length > 0
      ? `${STEP_WORD[focusSteps.length] ?? `${focusSteps.length} шага`} · ${activeName}`
      : finalTestPending
        ? `Финальный тест · ${activeName}`
        : `Сегодня · ${activeName}`;

  return (
    <NavShell screen="dashboard" onNav={handleNav}>
      <div className="screen">
        <SkipLink />
        <Topbar
          crumb={[
            { label: 'Studio', onClick: onLogo },
            { label: 'Дом', here: true },
          ]}
          onLogo={onLogo}
          showTimer={false}
        />
      <main id="main-content" className="dash-main" tabIndex={-1}>
        {/* HERO · date pill + greet + subtext */}
        <section className="dash-hero">
          <div className="dash-meta">
            <span className="dash-day-num">{dayNum}</span>
            <div className="dash-day-tx">
              <div>{todayLabel}</div>
              {/* H.4-EXT3.3 (17 MAY 2026): «Sprint X · день Y / Z» subtitle dropped
                  per Genie round 3 pick. Day-counter felt redundant with sprint
                  context already visible in topbar breadcrumb + Прогресс курса KPI. */}
            </div>
          </div>
          <h1 className="dash-greet">
            Доброе утро,
            <br />
            <span className="accent">инженер.</span>
          </h1>
          <p className="dash-greet-sub">
            {empty
              ? 'Начни с первого задания — оно объяснит, как тут всё устроено.'
              : firstStep
                ? `Сегодня в фокусе — ${firstStep.title}. Подсказка от Buddy уже ждёт.`
                : finalTestPending
                  ? 'Все тикеты закрыты — осталось сдать финальный тест спринта.'
                  : 'Все шаги сделаны. Можно выбрать следующий тикет в спринте.'}
          </p>
          {/* slot_t00_ftue_rewrite (6 JUN 2026) · external tester round: dominant
              «Начни здесь» CTA for the brand-new user. Shown until the first
              ticket is closed; opens T00 directly. Inline styles by design —
              styles/* is slot_mobile_readability territory (no new CSS classes). */}
          {startHereVisible && (
            <div
              data-testid="dash-start-here"
              style={{
                background: 'var(--surface)',
                border: '1.5px solid var(--ink)',
                borderRadius: 10,
                boxShadow: 'var(--shadow-md)',
                padding: '14px 16px',
                marginTop: 14,
                maxWidth: 420,
              }}
            >
              <div
                style={{
                  fontFamily: 'Space Mono, monospace',
                  fontSize: 10,
                  letterSpacing: 1,
                  textTransform: 'uppercase',
                  color: 'var(--teal)',
                }}
              >
                Начни здесь
              </div>
              <div style={{ fontSize: 16, fontWeight: 600, margin: '4px 0 2px' }}>
                Твоё первое задание
              </div>
              <div style={{ fontSize: 12.5, color: 'var(--ink-soft)' }}>T00 · урок · ~7 мин</div>
              <button
                type="button"
                className="btn-primary"
                style={{ marginTop: 10 }}
                onClick={() => onTicket('T00')}
              >
                Открыть →
              </button>
            </div>
          )}
        </section>

        {/* KPI strip · 4 cards · H.4-EXT2.2 (17 MAY 2026 · Genie round 2 pivot):
            «На ревью» → «Время в фокусе» (Pomodoro мин · today)
            «Buddy подсказок» → «Прогресс курса» (% completion · closed/total) */}
        <section className="kpi-strip">
          <div className="kpi">
            <div className="accent-stripe" />
            <div className="lab">В работе</div>
            <div className="val">{inProgressCount}</div>
            <div className="delta">{empty ? '—' : 'тикетов сейчас'}</div>
          </div>
          <div className="kpi teal">
            <div className="accent-stripe" />
            <div className="lab">Время в фокусе</div>
            <div className="val">
              {focusMinutesToday}
              <span className="suf">мин</span>
            </div>
            <div className="delta">
              {focusMinutesToday === 0 ? 'сегодня — ещё нет' : 'сегодня'}
            </div>
          </div>
          <div className="kpi mustard">
            <div className="accent-stripe" />
            <div className="lab">За неделю</div>
            <div className="val">{weekTotal}</div>
            <div className="delta">
              {empty ? '—' : weekTotal === 0 ? 'без активности' : 'закрыто за 7 дней'}
            </div>
          </div>
          <div className="kpi plum">
            <div className="accent-stripe" />
            <div className="lab">Прогресс курса</div>
            <div className="val">
              {coursePct}
              <span className="suf">%</span>
            </div>
            <div className="delta">
              {empty
                ? `0 из ${totalTicketsCount}`
                : `${completedCount} из ${totalTicketsCount}`}
            </div>
          </div>
        </section>

        {/* BRIDGE · Вчера → Сегодня · absorbed from Morning · empty placeholder per Q-B1-3 b */}
        <section className="dash-bridge" aria-label="Вчера и сегодня">
          <div className="dash-bridge-col">
            <div className="lab">Вчера · сделано</div>
            {recentDone.length === 0 ? (
              <div className="row">
                <span className="ic">·</span>
                <span>Старт — закрой первый тикет, и он появится здесь.</span>
              </div>
            ) : (
              recentDone.map((t) => (
                <div key={t.id} className="row done">
                  <span className="ic">✓</span>
                  <span>
                    {t.id} · {t.title}
                  </span>
                </div>
              ))
            )}
          </div>
          <div className="dash-bridge-arrow" aria-hidden="true">
            →
          </div>
          <div className="dash-bridge-col">
            <div className="lab">Сегодня в очереди</div>
            {firstStep ? (
              <div className="row now">
                <span className="ic">●</span>
                <span>
                  <strong>{firstStep.id}</strong> · продолжить с {firstStepPct}%
                </span>
              </div>
            ) : null}
            {todayQueue.length === 0 && !firstStep ? (
              <div className="row queue">
                <span className="ic">·</span>
                <span>Открой спринт — Buddy предложит первый шаг.</span>
              </div>
            ) : (
              todayQueue.map((t, i) => (
                <div key={t.id} className="row queue">
                  <span className="ic">{i + 2}</span>
                  <span>
                    {t.id} · {t.title}
                  </span>
                </div>
              ))
            )}
          </div>
        </section>

        {/* Подсказка бадди · мобильная копия НАД планом (≤1100px), чтобы событие
            не тонуло под верстаком · mockup_home_homework_hybrid.html «Мобильный» */}
        {nudge && (
          <div className="dpn-mobile">
            <NudgeCard
              nudge={nudge}
              buddyVariant={buddyVar}
              onAction={handleNudgeAction}
              onDismiss={handleNudgeDismiss}
            />
          </div>
        )}

        {/* 2-COL · focus-plan main + верстак практики (slot_home_practice_widget).
            Колонки растянуты (align-items: stretch): верстак подстраивается
            по высоте под план+ритм, ось и хвост прижаты к низу карточки. */}
        <section className="dash-cols has-bench">
          <div className="dash-main-col">
            <div className="dash-focus">
              <div className="df-head">
                <div className="df-ey">План на сегодня</div>
                <h2 className="df-h">{planHeading}</h2>
              </div>
              {finalTestPending ? (
                <div className="df-empty">
                  <div className="df-empty-mark" aria-hidden="true">
                    →
                  </div>
                  <div className="df-empty-t">Все тикеты закрыты</div>
                  <div className="df-empty-d">
                    Осталось сдать финальный тест спринта, чтобы открыть следующий.
                  </div>
                  <button
                    type="button"
                    className="btn-primary"
                    onClick={() => (onFinalTest ?? onSprint)(activeSprintId)}
                  >
                    Перейти к финальному тесту →
                  </button>
                </div>
              ) : empty || focusSteps.length === 0 ? (
                <div className="df-empty">
                  <div className="df-empty-mark" aria-hidden="true">
                    ∅
                  </div>
                  <div className="df-empty-t">Плана пока нет</div>
                  <div className="df-empty-d">
                    Открой спринт — Buddy предложит первый шаг.
                  </div>
                  <button
                    type="button"
                    className="btn-primary"
                    onClick={() => onSprint(activeSprintId)}
                  >
                    Открыть спринт →
                  </button>
                </div>
              ) : (
                <ol className="df-steps">
                  {focusSteps.map((t, i) => {
                    const status = progress.tickets[t.id]?.status ?? 'todo';
                    const isFirst = i === 0;
                    const slotClass =
                      i === 0 ? 'now' : i === 1 ? 'next' : 'later';
                    const tag = typeToTag(t);
                    const numStr = (i + 1).toString().padStart(2, '0');
                    const pct = isFirst ? firstStepPct : 0;
                    const minLeft = isFirst
                      ? firstStepMinLeft
                      : t.estimateMin;
                    const meta = isFirst && pct > 0
                      ? `~${minLeft} мин · ${t.id} · ${pct}% прочитано`
                      : `~${minLeft} мин · ${t.id}${t.priority ? ` · ${t.priority}` : ''}`;
                    const statusLabel =
                      status === 'in-progress'
                        ? 'в работе'
                        : status === 'review'
                          ? 'нужно ревью'
                          : i === 2
                            ? 'опционально'
                            : 'следующий';
                    return (
                      <li key={t.id} className="df-step-li">
                        <CourseLink
                          route={{ screen: 'ticket', ticketId: t.id }}
                          onNavigate={() => onTicket(t.id)}
                          className={`df-step ${slotClass} course-link`}
                          aria-label={`${tag.label} · ${t.title}`}
                        >
                          <span className="df-num">{numStr}</span>
                          <div className="df-body">
                            <div className={`df-tag tag-${tag.badge}`}>
                              {tag.label} · {statusLabel}
                            </div>
                            <div className="df-t">{t.title}</div>
                            <div className="df-m">{meta}</div>
                          </div>
                          <span className="df-go" aria-hidden="true">
                            {i === 2 ? '·' : '→'}
                          </span>
                        </CourseLink>
                      </li>
                    );
                  })}
                </ol>
              )}
            </div>

            {/* «Ритм недели» · низкая карточка под планом (выбор Genie Ф1) */}
            <WeekRhythm
              days={rhythm.days}
              streak={practiceStats.streak}
              weekDone={practiceStats.week.done}
              weekGoal={practiceStats.week.goal}
              goalMin={practiceStats.daily.goalMin}
              activeMin={practiceStats.daily.activeMin}
              caption={rhythm.caption}
            />
          </div>

          {/* H.4-EXT2.1 (17 MAY 2026): dash-side aside dropped per Genie.
              10 JUL 2026 (slot_home_practice_widget): правая колонка вернулась —
              но только под практику: верстак дня + слот подсказки бадди. */}
          <div className="dash-practice-col">
            <PracticeBench
              sprintLabel={activeSprintId}
              doneCount={benchDoneCount}
              totalCount={activeSprintHw.length}
              nudge={nudge}
              buddyVariant={buddyVar}
              rec={benchRec}
              clearText={benchClearText}
              weakAxis={benchWeakAxis}
              tail={benchTail}
              onOpenPractice={goPractice}
              onNudgeAction={handleNudgeAction}
              onNudgeDismiss={handleNudgeDismiss}
            />
          </div>
        </section>
      </main>
      </div>
    </NavShell>
  );
}
